/**
 * field_guide_catalog.ts — THE FIELD GUIDE's species (2026-09-24). MIRRORED byte-identical at
 * client/src/lib/field_guide_catalog.ts (the harness diffs the two).
 *
 * Four books: BIRDS (observed — watch one through the glass a moment), BUGS (caught with the net), CREATURES
 * (observed at a distance or hunted) and FISH (caught on a line). Birds, bugs and creatures are the world's own
 * species (lib/birds.ts, lib/bugs.ts, the wilds' SPECIES); the fish are new, each with the water it lives in, the
 * hours it bites, the weather it likes and its rarity (Palia's and Animal Crossing's lesson: WHEN and WHERE make a
 * collection — the rare ones are a reason to come back at night, in the rain, to the sea).
 *
 * `rollFish` is the one roll: the server casts it (authoritative — the client never picks its fish), the harness
 * and the guide's hints read the same table.
 */

export type GuideKind = 'bird' | 'bug' | 'creature' | 'fish';
export const GUIDE_KINDS: GuideKind[] = ['bird', 'bug', 'creature', 'fish'];
export type Water = 'sea' | 'lake' | 'river';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic';

export interface GuideEntry { key: string; name: string; blurb: string }
export interface FishSpec extends GuideEntry {
  waters: Water[];
  /** Day windows it bites in (fractions of the day, 0 = midnight): [from, to], wrapping past midnight when from > to. */
  hours: Array<[number, number]>;
  /** Weather it needs ('' = any): the server's sky_state weather. */
  weather: string[];
  /** Biomes it needs (empty = any). */
  biomes: string[];
  rarity: Rarity;
  /** Length range in centimetres (a manta: its span). */
  cm: [number, number];
  /** How hard it fights: the reel minigame's pace (1 easy … 4 hard). */
  fight: number;
}

export const BIRDS: GuideEntry[] = [
  { key: 'jay', name: 'Cobalt Jay', blurb: 'A loud blue flash between the trees. Forages on the ground in small bands and flushes at a step.' },
  { key: 'gull', name: 'Pale Gull', blurb: 'The coast and the lakes belong to it. Soars on still wings, drinks at the shore, follows the fishing boats.' },
  { key: 'hawk', name: 'Amber Hawk', blurb: 'Alone, high, in slow circles. Roosts on the giant trees and never lands at the water.' },
  { key: 'starling', name: 'Sootwing Starling', blurb: 'The city bird. Rooftops by day; at the turn of the seasons the skeins cross the whole sky.' },
];
export const BUGS: GuideEntry[] = [
  { key: 'gnat', name: 'Woodland Gnat', blurb: 'The daytime swarm under the canopy. Stand still and they come to you.' },
  { key: 'moth', name: 'Dusk Moth', blurb: 'Out at dusk, drawn to lamps and fires. Pale wings, a slow drunk flight.' },
  { key: 'firefly', name: 'Emberfly', blurb: 'The night woods blink with them — two short pulses and a dark pause.' },
];
export const CREATURES: GuideEntry[] = [
  { key: 'ridge_stalker', name: 'Ridge Stalker', blurb: 'Hunts the highlands and the plains in threes. Quick to anger, quick to quit.' },
  { key: 'spore_drone', name: 'Spore Drone', blurb: 'Drifts over the fungal basins and the wetlands. Fragile, but a swarm stings.' },
  { key: 'duneback_brute', name: 'Duneback Brute', blurb: 'A slow wall of armour in the dunes. Leave it be unless you mean it.' },
  { key: 'mire_lurker', name: 'Mire Lurker', blurb: 'Waits in the wetland reeds and comes fast when it comes.' },
  { key: 'crag_wyrm', name: 'Crag Wyrm', blurb: 'The largest thing in the wilds. Crags and deserts; a den of two at most.' },
  { key: 'glass_shrike', name: 'Glass Shrike', blurb: 'Crystal forests and bright plains. Hunts from far off, and far faster than you.' },
];

const DAY: [number, number] = [0.25, 0.75];
const NIGHT: [number, number] = [0.78, 0.22];
const ANY: [number, number] = [0, 1];
const DUSK: [number, number] = [0.7, 0.82];
const DAWN: [number, number] = [0.18, 0.3];

export const FISH: FishSpec[] = [
  { key: 'silverfin', name: 'Silverfin', blurb: 'The sea\'s everyday catch — quick, bright and good eating.', waters: ['sea'], hours: [ANY], weather: [], biomes: [], rarity: 'common', cm: [20, 45], fight: 1 },
  { key: 'lanternfish', name: 'Lanternfish', blurb: 'Rises from the deep at night, its belly lights blinking like a town seen from the sky.', waters: ['sea'], hours: [NIGHT], weather: [], biomes: [], rarity: 'uncommon', cm: [8, 15], fight: 2 },
  { key: 'duskeel', name: 'Duskeel', blurb: 'Bites at dusk and dawn, where the rivers meet the sea. Slippery to the last.', waters: ['sea', 'river'], hours: [DUSK, DAWN], weather: [], biomes: [], rarity: 'uncommon', cm: [50, 120], fight: 3 },
  { key: 'amber_carp', name: 'Amber Carp', blurb: 'Fat and golden in the still lakes on a sunny day.', waters: ['lake'], hours: [DAY], weather: [], biomes: [], rarity: 'common', cm: [30, 70], fight: 1 },
  { key: 'glass_trout', name: 'Glass Trout', blurb: 'So clear you can count its bones. Holds in the river current by day.', waters: ['river'], hours: [DAY], weather: [], biomes: [], rarity: 'common', cm: [25, 55], fight: 2 },
  { key: 'reedpike', name: 'Reedpike', blurb: 'A toothy ambusher of the reed beds. Takes a lure at any hour.', waters: ['lake', 'river'], hours: [ANY], weather: [], biomes: [], rarity: 'uncommon', cm: [50, 110], fight: 3 },
  { key: 'mudsnapper', name: 'Mudsnapper', blurb: 'Comes up from the bottom only when the rain stirs the lake.', waters: ['lake'], hours: [ANY], weather: ['rain', 'storm'], biomes: [], rarity: 'common', cm: [30, 80], fight: 2 },
  { key: 'starscale_koi', name: 'Starscale Koi', blurb: 'Night lakes only. The gold on its patches is not a trick of the moon.', waters: ['lake'], hours: [NIGHT], weather: [], biomes: [], rarity: 'rare', cm: [40, 75], fight: 3 },
  { key: 'stormray', name: 'Stormray', blurb: 'A sea giant that only surfaces in a storm, lightning running down its back.', waters: ['sea'], hours: [ANY], weather: ['storm'], biomes: [], rarity: 'epic', cm: [90, 200], fight: 4 },
  { key: 'ember_guppy', name: 'Ember Guppy', blurb: 'A flicker of flame in the warm desert rivers.', waters: ['river'], hours: [ANY], weather: [], biomes: ['dune_desert', 'regolith_plains', 'rocky_highlands'], rarity: 'common', cm: [4, 8], fight: 1 },
];

export const RARITY_WEIGHT: Record<Rarity, number> = { common: 10, uncommon: 4, rare: 1.5, epic: 0.8 };

export function guideBook(kind: GuideKind): GuideEntry[] {
  return kind === 'bird' ? BIRDS : kind === 'bug' ? BUGS : kind === 'creature' ? CREATURES : FISH;
}
export function guideEntry(kind: GuideKind, key: string): GuideEntry | null {
  return guideBook(kind).find((e) => e.key === key) ?? null;
}
export function fishSpec(key: string): FishSpec | null { return FISH.find((f) => f.key === key) ?? null; }

function inWindow(tod: number, w: [number, number]): boolean {
  const [a, b] = w;
  return a <= b ? tod >= a && tod <= b : tod >= a || tod <= b;
}

/** Is a fish biting here, now? (the guide's "when and where" and the roll's filter share it) */
export function fishBites(f: FishSpec, water: Water, tod: number, weather: string, biome: string): boolean {
  if (!f.waters.includes(water)) return false;
  if (!f.hours.some((w) => inWindow(tod, w))) return false;
  if (f.weather.length > 0 && !f.weather.includes(weather)) return false;
  if (f.biomes.length > 0 && !f.biomes.includes(biome)) return false;
  return true;
}

/**
 * The catch: a species by weight among those biting here and now, and its length. `r` is a 0..1 stream (the server
 * seeds it per cast). Never empty: every water has an any-hour common.
 */
export function rollFish(water: Water, tod: number, weather: string, biome: string, r: () => number): { key: string; cm: number } {
  const pool = FISH.filter((f) => fishBites(f, water, tod, weather, biome));
  const list = pool.length > 0 ? pool : FISH.filter((f) => f.waters.includes(water));
  let total = 0;
  for (const f of list) total += RARITY_WEIGHT[f.rarity];
  let x = r() * total;
  let pick = list[0];
  for (const f of list) { x -= RARITY_WEIGHT[f.rarity]; if (x <= 0) { pick = f; break; } }
  // Lengths lean small (most fish are ordinary); a trophy is the tail of the curve.
  const u = r();
  const cm = Math.round((pick.cm[0] + (pick.cm[1] - pick.cm[0]) * u * u) * 10) / 10;
  return { key: pick.key, cm };
}

/** The bite delay (ms) for a cast — the wait on the float — from 3 to 12 s. */
export function biteDelayMs(r: () => number): number { return 3000 + Math.floor(r() * 9000); }
